/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { get, action, computed } from '@ember/object';
import { not, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DepartureReason from 'linced-app/enums/nz-moe-departure-reason';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import JurisdictionalStatus from 'linced-app/enums/jurisdictional-status';
import NzMoeActivity from 'linced-app/enums/nz-moe-post-school-activity';
import NzMoeArea from 'linced-app/enums/nz-moe-area';
import NzMoeSchool from '@linc-technologies/linced-common/enums/nz-moe-school';
import PersonJurisdiction from 'linced-app/enums/person-jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { observes } from '@ember-decorators/object';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class ManageController extends Controller {
  // Services
  @service
  features;

  @service
  fetch;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  toaster;

  DepartureReason = DepartureReason; // TODO: Jurisdictional switching
  EnrolmentStatus = EnrolmentStatus;

  Jurisdiction = Jurisdiction;

  JurisdictionalStatus = JurisdictionalStatus;

  NzMoeActivity = NzMoeActivity;

  NzMoeArea = NzMoeArea;

  NzMoeSchool = NzMoeSchool;

  PersonJurisdiction = PersonJurisdiction;

  Scopes = Scopes;

  // Disable ENROL arrival/leaver uploads unless specifically enabled
  // for the school, to facilitate approval testing without full release.
  @optionAlias('hero:nzMOE:enrolArrivalsLeaversEnabled')
  nzMoeEnrolArrivalsLeaversEnabled;

  @not('nzMoeEnrolArrivalsLeaversEnabled')
  enrolArrivalsLeaversDisabled;

  @computed(
    'session.school.jurisdiction',
    'person.jurisdiction',
    'features.multiJurisdictionalEnabled',
  )
  get jurisdiction() {
    return this.get('features.multiJurisdictionalEnabled')
      ? this.get('person.jurisdiction')
      : this.get('session.school.jurisdiction');
  }

  @computed('model.learnerFields.@each.inputType')
  get jurisdictionalIdField() {
    return this.model.learnerFields.find(
      (item) => item.inputType === InputType.JURISDICTIONAL_ID,
    );
  }

  @alias('model.person')
  person;

  @computed('model.enrolments.[]', 'person.learner.enrolmentId')
  get enrolment() {
    return this.model.enrolments.find(
      (item) => item.id === this.get('person.learner.enrolmentId'),
    );
  }

  @computed('enrolment.status')
  get currentStatus() {
    switch (this.get('enrolment.status')) {
      case EnrolmentStatus.PRE_ENROLLED:
        return 'Pre-Enrolled';
      case EnrolmentStatus.PENDING:
        return 'Pending Enrolment';
      case EnrolmentStatus.CURRENT:
        return 'Enrolled';
      case EnrolmentStatus.FORMER:
        return 'Not Enrolled';
      case EnrolmentStatus.ARCHIVED:
        return 'Archived';
    }

    return 'Unknown';
  }

  @computed('enrolment.status')
  get buttons() {
    let buttons = [EnrolmentStatus.PRE_ENROLLED];

    switch (this.get('enrolment.status')) {
      case EnrolmentStatus.PRE_ENROLLED:
        buttons = [
          EnrolmentStatus.CURRENT,
          EnrolmentStatus.FORMER,
          EnrolmentStatus.ARCHIVED,
        ];
        break;
      case EnrolmentStatus.CURRENT:
      case EnrolmentStatus.PENDING:
        buttons = [EnrolmentStatus.PRE_ENROLLED, EnrolmentStatus.FORMER];
        break;
      case EnrolmentStatus.FORMER:
        buttons = [EnrolmentStatus.PRE_ENROLLED, EnrolmentStatus.ARCHIVED];
        break;
      case EnrolmentStatus.ARCHIVED:
        buttons = [];
        break;
    }

    return buttons;
  }

  @tracked
  otherSchool = null;

  fetchingSchools = [];

  @observes('enrolment.status', 'person.learner.enrolments.[]')
  fetchOtherSchool() {
    this.otherSchool = null;
    // The following line uses `get()` to prevent errors when `enrolment` isn't set (i.e. record not found).
    if (this.get('enrolment.status') === EnrolmentStatus.PENDING) {
      let current = findBy(
        this.get('person.learner.enrolments'),
        'status',
        EnrolmentStatus.CURRENT,
      );
      if (
        !isEmpty(current) &&
        !this.fetchingSchools.includes(current.schoolId)
      ) {
        this.fetchingSchools = [...this.fetchingSchools, current.schoolId];
        this.store.findRecord('school', current.schoolId).then((school) => {
          this.otherSchool = school;
          this.fetchingSchools = this.fetchingSchools.filter(
            (schoolId) => schoolId !== current.schoolId,
          );
        });
      }
    }
  }

  @computed('enrolment.id', 'model.enrolments.[]')
  get previousEnrolments() {
    if (isEmpty(this.enrolment)) {
      return [];
    }
    return this.model.enrolments
      .filter((enrolment) => {
        // Only enrolments from this school will be returned, so just exclude the current enrolment (unless former).
        return (
          enrolment.status === EnrolmentStatus.FORMER ||
          enrolment.id !== this.enrolment.id
        );
      })
      .sort(byKeys('exitDate', 'entryDate', 'createTime'))
      .reverse();
  }

  @computed('model.{enrolments.[],applications.[]}')
  get sortedApplications() {
    return this.model.applications
      .slice()
      .sort(
        (a, b) =>
          (get(a, 'enrolment.startDate') || '').localeCompare(
            get(b, 'enrolment.startDate') || '',
          ) || a.createTime - b.createTime,
      )
      .reverse();
  }

  newStatus = EnrolmentStatus.PRE_ENROLLED;

  @tracked
  revertToPreEnrolDialogOpen = false;

  @tracked
  preEnrolDialogOpen = false;

  @tracked
  expectedStartDate = '';

  @action
  setExpectedStartDate(value) {
    this.expectedStartDate = value;
  }

  @tracked
  enrolDialogOpen = false;

  @tracked
  unenrolDialogOpen = false;

  @tracked
  archiveDialogOpen = false;

  @tracked
  archiveInProgress = false;

  @tracked
  arrivalUploadDialogOpen = false;

  @tracked
  leaverUploadDialogOpen = false;

  @tracked
  editingStartDate = false;

  @action
  setEditingStartDate(value) {
    this.editingStartDate = value;
  }

  @tracked
  enrolmentToEdit = null;

  @tracked
  editingEnrolmentDialogOpen = false;

  @tracked
  hasAttendanceRecords = false;

  @tracked
  hasOutstandingBalance = false;

  @tracked
  canBeArchived = true;

  @tracked
  checkingIsArchivable = false;

  @tracked
  removeLastAttendanceDateDialogOpen = false;

  @tracked
  savingEnrolment;

  @action
  changeStatus(newStatus) {
    switch (newStatus) {
      case EnrolmentStatus.PRE_ENROLLED:
        if (this.enrolment.status === EnrolmentStatus.FORMER) {
          this.preEnrolDialogOpen = true;
        } else {
          this.revertToPreEnrolDialogOpen = true;
          this.expectedStartDate = '';
        }
        break;
      case EnrolmentStatus.CURRENT:
        this.enrolDialogOpen = true;
        break;
      case EnrolmentStatus.FORMER:
        this.unenrolDialogOpen = true;
        break;
      case EnrolmentStatus.ARCHIVED:
        this.archiveDialogOpen = true;
        this.checkIfArchivable();
        break;
    }
  }

  @action
  closePreEnrolDialog() {
    this.person.rollbackAttributes();
    this.enrolment.rollbackAttributes();
    this.preEnrolDialogOpen = false;
  }

  @action
  preEnrolmentComplete() {
    this.preEnrolDialogOpen = false;
    this.model.enrolments.update().then(() => {
      this.person.reload();
    });
  }

  @action
  closeEnrolDialog() {
    this.person.rollbackAttributes();
    this.enrolment.rollbackAttributes();
    this.enrolDialogOpen = false;
  }

  @action
  enrolmentComplete() {
    this.enrolDialogOpen = false;
    this.person.reload();
  }

  @action
  closeRevertToPreEnrolDialog() {
    this.revertToPreEnrolDialogOpen = false;
  }

  @action
  revertToPreEnrol() {
    // Update the enrolment record before saving to ensure that we don't accidentally wipe anything that's changed.
    this.enrolment.reload().then((enrolment) => {
      enrolment.set('status', EnrolmentStatus.PRE_ENROLLED);
      enrolment.set('jurisdictionalStatus', JurisdictionalStatus.UNSPECIFIED);
      enrolment.set('entryDate', this.expectedStartDate);
      enrolment.save().then(() => {
        this.revertToPreEnrolDialogOpen = false;
      });
    });
  }

  @action
  closeUnenrolDialog(upload) {
    this.unenrolDialogOpen = false;
    this.person.reload();
    if (upload) {
      this.leaverUploadDialogOpen = true;
    }
  }

  @action
  closeArchiveDialog(archive) {
    if (archive) {
      this.archiveInProgress = true;
      this.enrolment.reload().then((enrolment) => {
        enrolment.set('status', EnrolmentStatus.ARCHIVED);
        enrolment.save().then(() => {
          this.archiveDialogOpen = false;
          this.archiveInProgress = false;
        });
      });
    } else {
      this.archiveDialogOpen = false;
    }
  }

  @action
  openArrivalUploadDialog() {
    this.arrivalUploadDialogOpen = true;
  }

  @action
  closeArrivalUploadDialog() {
    this.arrivalUploadDialogOpen = false;
  }

  @action
  openLeaverUploadDialog() {
    this.leaverUploadDialogOpen = true;
  }

  @action
  closeLeaverUploadDialog() {
    this.leaverUploadDialogOpen = false;
  }

  @action
  saveEnrolmentStartDate(enrolment) {
    this.savingEnrolment = true;
    // Retrieve a fresh copy of the enrolment to ensure we're not making any unintended state changes etc
    let entryDate = enrolment.entryDate;
    enrolment.reload().then((enrolment) => {
      enrolment.set('entryDate', entryDate);
      enrolment.save().then(() => {
        this.editingStartDate = false;
        this.savingEnrolment = false;
      });
    });
  }

  @action
  editEnrolment(enrolment) {
    this.enrolmentToEdit = enrolment;
    this.editingEnrolmentDialogOpen = true;
  }

  @action
  closeEditingEnrolmentDialog() {
    this.enrolmentToEdit = null;
    this.editingEnrolmentDialogOpen = false;
  }

  @action
  saveJurisdiction(jurisdiction) {
    this.person.reload().then((person) => {
      person.set('jurisdiction', jurisdiction.value);
      person
        .save()
        .then(() => {
          this.toaster.success('Student Jurisdiction updated successfully.');
        })
        .catch(() => {
          this.toaster.error('Error updating Student Jurisdiction.');
        });
    });
  }

  @action
  removeLastAttendanceDate() {
    this.enrolment.set('exitDate', '');
    this.enrolment
      .save()
      .then(() => {
        this.toaster.success('Last Attendance Date removed successfully!');
      })
      .catch(() => {
        this.toaster.error('Error removing Last Attendance Date!');
      })
      .finally(() => {
        this.removeLastAttendanceDateDialogOpen = false;
      });
  }

  // checkIfArchivable checks if student can be archived or not
  checkIfArchivable() {
    this.checkingIsArchivable = true;
    let attendanceParams = {
      schoolId: this.get('session.activeSchool'),
      arrivals: [
        {
          personId: this.person.id,
          entryDate: moment(this.enrolment.createTime * 1000).format(
            'YYYY-MM-DD',
          ),
        },
      ],
    };
    let attendanceOptions = {
      data: JSON.stringify(attendanceParams),
    };
    let financeParams = {
      schoolId: this.get('session.activeSchool'),
      personId: this.person.id,
    };
    let financeOptions = {
      data: JSON.stringify(financeParams),
    };
    let attendanceUrl = `${config.APP.apiHost}/attendance/v4/arrivals/nzmoe/:validate`;
    let financeUrl = `${config.APP.apiHost}/finance/v4/accounts/:checkOutstandingBalance`;
    hash({
      attendance: this.fetch.post(attendanceUrl, attendanceOptions),
      accountCheck: this.fetch.post(financeUrl, financeOptions),
    })
      .then((data) => {
        let attendanceResults = get(data.attendance, 'results') ?? [];
        let attendance = attendanceResults.find(
          (item) => item.personId === this.get('person.id'),
        );
        this.hasAttendanceRecords = attendance.isValid;
        this.hasOutstandingBalance = data.accountCheck.hasOutstandingBalance;

        this.canBeArchived = !(
          attendance.isValid || data.accountCheck.hasOutstandingBalance
        );
      })
      .catch(() => {
        this.canBeArchived = false;
      })
      .finally(() => {
        this.checkingIsArchivable = false;
      });
  }

  reset() {
    this.revertToPreEnrolDialogOpen = false;
    this.preEnrolDialogOpen = false;
    this.enrolDialogOpen = false;
    this.unenrolDialogOpen = false;
    this.arrivalUploadDialogOpen = false;
    this.leaverUploadDialogOpen = false;
    this.editingStartDate = false;
    this.hasAttendanceRecords = false;
    this.hasOutstandingBalance = false;
    this.canBeArchived = true;
    this.checkingIsArchivable = false;
  }
}
